import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Boxes,
  Network,
  Workflow,
  Sparkles,
  Database,
  Globe2,
  ShieldCheck,
  PlayCircle,
  Layers,
  Cpu,
  GitBranch,
  BarChart3,
  Plug,
  Gauge,
  Building2,
  TrendingUp,
  Code2,
  BrainCircuit,
  Users,
  CreditCard,
  HeartPulse,
  PiggyBank,
  Award,
  Coins,
  ShieldAlert,
  TrendingDown,
  Cloud,
  Check,
  X,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { VideoModal } from "@/components/landing/VideoModal";
import { SEO } from "@/components/SEO";

/* Electric blue accent used only on this page */
const ACCENT = "#0EA5E9"; // sky-500
const DEEP = "#0B1F3A";

const trust = [
  { value: "160M+", label: "Policies & Members Processed Annually" },
  { value: "20+", label: "Insurers" },
  { value: "120+", label: "Product Variants" },
];

export default function PlatformPage() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <main className="bg-white text-ink" data-testid="platform-page">
      <SEO
        title="Platform — Unified Insurance Operating Platform"
        description="A unified insurance operating platform powering policy administration, distribution, workflows, analytics and AI-led operations at national scale."
        path="/platform"
      />
      <Nav />

      {/* HERO */}
      <section className="relative bg-white overflow-hidden">
        {/* subtle background depth */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 70% 20%, rgba(14,165,233,0.10), transparent 60%), radial-gradient(ellipse 60% 40% at 20% 90%, rgba(11,31,58,0.05), transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(11,31,58,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,58,0.04) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 75%)",
          }}
        />

        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              {/* Eyebrow pill */}
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
                style={{ background: "rgba(14,165,233,0.08)", borderColor: "rgba(14,165,233,0.20)" }}
                data-testid="platform-eyebrow"
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
                <span
                  className="text-[11px] font-medium uppercase tracking-[0.2em]"
                  style={{ color: ACCENT }}
                >
                  Composable · AI-Ready · Operating Platform
                </span>
              </div>

              {/* Headline */}
              <h1
                data-testid="platform-hero-title"
                className="font-display font-medium text-4xl sm:text-5xl md:text-6xl lg:text-[68px] tracking-tight leading-[1.02] mt-8"
                style={{ color: DEEP }}
              >
                The Insurance Operating Platform
                <br />
                Built for{" "}
                <span style={{ color: ACCENT }}>Growth, Scale & AI.</span>
              </h1>

              {/* Subheadline */}
              <p className="mt-8 text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[640px]">
                Gradatim unifies core policy administration, digital distribution,
                workflow orchestration and embedded AI into a single operating
                platform for modern insurers.
              </p>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/#contact"
                  data-testid="platform-cta-primary"
                  className="group inline-flex items-center justify-center gap-3 text-white px-7 py-3.5 rounded-md text-sm font-medium tracking-wide transition-all shadow-md hover:shadow-xl"
                  style={{ background: DEEP }}
                >
                  Request a demo
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  data-testid="platform-cta-secondary"
                  className="group inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-md text-sm font-medium tracking-wide transition-all border-2"
                  style={{ borderColor: DEEP, color: DEEP }}
                >
                  <PlayCircle className="h-4 w-4" />
                  Explore the platform
                </button>
              </div>

              {/* Trust strip */}
              <div
                className="mt-14 flex flex-wrap items-center gap-x-8 md:gap-x-12 gap-y-4 pt-7 border-t"
                style={{ borderColor: "rgba(11,31,58,0.08)" }}
                data-testid="platform-trust-strip"
              >
                {trust.map((t, i) => (
                  <div key={t.value} className="flex items-center gap-4">
                    <div>
                      <div
                        className="font-display text-2xl md:text-[26px] tracking-tight leading-none"
                        style={{ color: DEEP }}
                      >
                        {t.value}
                      </div>
                      <div className="text-[12px] text-slate-500 mt-1 leading-snug max-w-[180px]">
                        {t.label}
                      </div>
                    </div>
                    {i < trust.length - 1 && (
                      <span
                        aria-hidden
                        className="h-10 w-px hidden md:inline-block"
                        style={{ background: "rgba(11,31,58,0.10)" }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* RIGHT — Architectural visual */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5"
            >
              <PlatformArchitectureVisual />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — PLATFORM POSITIONING */}
      <PlatformPositioning />

      {/* SECTION 3 — PLATFORM ARCHITECTURE DIAGRAM */}
      <PlatformArchitecture />

      {/* SECTION 4 — BUSINESS OUTCOMES */}
      <BusinessOutcomes />

      {/* SECTION 5 — INDUSTRY USE CASES */}
      <IndustryUseCases />

      {/* SECTION 6 — GRADATIM INTELLIGENCE (AI) */}
      <GradatimIntelligence />

      {/* SECTION 7 — SCALE & PROOF */}
      <ScaleAndProof />

      {/* SECTION 8 — MODERNIZATION / WHY GRADATIM */}
      <Modernization />

      {/* SECTION 9 — FINAL CTA */}
      <FinalCTA />

      <Footer />

      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        src="/platform-video.mp4"
        title="Gradatim Platform — Walkthrough"
      />
    </main>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Architectural platform visual:
   Layered glassmorphism cards with connection lines, channel→core→AI
   ───────────────────────────────────────────────────────────────────── */
const PlatformArchitectureVisual = () => {
  return (
    <div className="relative w-full max-w-[540px] mx-auto aspect-[5/6]">
      {/* Soft glow */}
      <div
        aria-hidden
        className="absolute -inset-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(14,165,233,0.18), transparent 65%)",
        }}
      />

      {/* Outer frame */}
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: "linear-gradient(180deg, rgba(11,31,58,0.02) 0%, rgba(14,165,233,0.04) 100%)",
          border: "1px solid rgba(11,31,58,0.08)",
        }}
      />

      {/* Connection lines (SVG) */}
      <svg
        viewBox="0 0 540 648"
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden
      >
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        {/* L1 → L2 verticals */}
        <line x1="120" y1="135" x2="200" y2="270" stroke="url(#lineGrad)" strokeWidth="1.2" />
        <line x1="270" y1="135" x2="270" y2="270" stroke="url(#lineGrad)" strokeWidth="1.2" />
        <line x1="420" y1="135" x2="340" y2="270" stroke="url(#lineGrad)" strokeWidth="1.2" />
        {/* L2 → L3 */}
        <line x1="200" y1="345" x2="170" y2="455" stroke="url(#lineGrad)" strokeWidth="1.2" />
        <line x1="270" y1="345" x2="270" y2="455" stroke="url(#lineGrad)" strokeWidth="1.2" />
        <line x1="340" y1="345" x2="370" y2="455" stroke="url(#lineGrad)" strokeWidth="1.2" />
        {/* L3 → L4 */}
        <line x1="170" y1="535" x2="270" y2="580" stroke="url(#lineGrad)" strokeWidth="1.2" />
        <line x1="270" y1="535" x2="270" y2="580" stroke="url(#lineGrad)" strokeWidth="1.2" />
        <line x1="370" y1="535" x2="270" y2="580" stroke="url(#lineGrad)" strokeWidth="1.2" />
      </svg>

      {/* LAYER 1 — Channels (3 small cards top) */}
      <div className="absolute top-[10%] left-0 right-0 flex justify-around px-6">
        {[
          { Icon: Globe2, label: "Channels" },
          { Icon: Network, label: "Distribution" },
          { Icon: ShieldCheck, label: "Embedded" },
        ].map((c, i) => (
          <GlassCard key={c.label} delay={i * 0.06} compact>
            <c.Icon className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={1.8} />
            <span className="text-[11px] font-medium" style={{ color: DEEP }}>
              {c.label}
            </span>
          </GlassCard>
        ))}
      </div>

      {/* LAYER 2 — Core (3 medium cards) */}
      <div className="absolute top-[34%] left-0 right-0 flex justify-around px-4">
        {[
          { Icon: Boxes, label: "Policy Admin" },
          { Icon: Workflow, label: "Underwriting" },
          { Icon: Database, label: "Claims" },
        ].map((c, i) => (
          <GlassCard key={c.label} delay={0.2 + i * 0.06} mid>
            <c.Icon className="h-4 w-4" style={{ color: ACCENT }} strokeWidth={1.8} />
            <span className="text-[12px] font-medium" style={{ color: DEEP }}>
              {c.label}
            </span>
          </GlassCard>
        ))}
      </div>

      {/* LAYER 3 — AI / Cognition (3 cards) */}
      <div className="absolute top-[60%] left-0 right-0 flex justify-around px-6">
        {[
          { Icon: Sparkles, label: "AI Decisions" },
          { Icon: Sparkles, label: "Cognition" },
          { Icon: Sparkles, label: "Automation" },
        ].map((c, i) => (
          <GlassCard key={c.label} delay={0.36 + i * 0.06} compact>
            <c.Icon className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={1.8} />
            <span className="text-[11px] font-medium" style={{ color: DEEP }}>
              {c.label}
            </span>
          </GlassCard>
        ))}
      </div>

      {/* LAYER 4 — Outcomes (one prominent card at bottom) */}
      <div className="absolute top-[83%] left-1/2 -translate-x-1/2">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7 }}
          className="px-5 py-3 rounded-xl shadow-lg backdrop-blur-md"
          style={{
            background: `linear-gradient(135deg, ${DEEP} 0%, #1E3A5F 100%)`,
            border: "1px solid rgba(14,165,233,0.35)",
          }}
        >
          <div className="flex items-center gap-2.5">
            <span
              className="h-1.5 w-1.5 rounded-full animate-pulse"
              style={{ background: ACCENT }}
            />
            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white">
              Operating Platform
            </span>
          </div>
        </motion.div>
      </div>

      {/* Pulsing data dots along lines */}
      <DataPulse top="22%" left="48%" delay={0} />
      <DataPulse top="50%" left="48%" delay={1.2} />
      <DataPulse top="74%" left="48%" delay={2.4} />
    </div>
  );
};

const GlassCard = ({ children, delay = 0, compact = false, mid = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className={`inline-flex items-center gap-2 backdrop-blur-md shadow-sm ${
      mid ? "px-3.5 py-2.5" : "px-3 py-2"
    } rounded-lg`}
    style={{
      background: "rgba(255,255,255,0.85)",
      border: "1px solid rgba(14,165,233,0.22)",
    }}
  >
    {children}
  </motion.div>
);

const DataPulse = ({ top, left, delay }) => (
  <motion.div
    className="absolute h-2 w-2 rounded-full pointer-events-none"
    style={{ top, left, background: ACCENT, boxShadow: `0 0 12px ${ACCENT}` }}
    initial={{ opacity: 0, scale: 0.5 }}
    animate={{ opacity: [0, 1, 0], scale: [0.5, 1.4, 0.5] }}
    transition={{ duration: 2.4, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);


/* ─────────────────────────────────────────────────────────────────────
   SECTION 2 — PLATFORM POSITIONING
   Unified Insurance Operating Platform — 5-layer architecture stack
   ───────────────────────────────────────────────────────────────────── */

const LAYERS = [
  {
    n: "01",
    Icon: Globe2,
    title: "Experience & Channels",
    items: [
      "Employer Portals",
      "Bancassurance",
      "Embedded Insurance",
      "Broker Platforms",
      "APIs",
      "Mobile Journeys",
    ],
    bg: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
    border: "rgba(15, 23, 42, 0.08)",
    titleColor: "#0B1F3A",
    chipBg: "rgba(255,255,255,0.9)",
    chipBorder: "rgba(15,23,42,0.10)",
    chipColor: "#334155",
    tone: "neutral",
  },
  {
    n: "02",
    Icon: Boxes,
    title: "Core Insurance Services",
    items: [
      "Policy Administration",
      "Underwriting",
      "Claims",
      "Renewals",
      "Product Configuration",
      "Reinsurance",
      "Accounting",
    ],
    bg: "linear-gradient(180deg, #EFF6FF 0%, #DBEAFE 100%)",
    border: "rgba(14, 165, 233, 0.18)",
    titleColor: "#0B1F3A",
    chipBg: "rgba(255,255,255,0.92)",
    chipBorder: "rgba(14,165,233,0.20)",
    chipColor: "#0B1F3A",
    tone: "operational",
  },
  {
    n: "03",
    Icon: GitBranch,
    title: "Workflow & Rules Engine",
    items: [
      "Maker-Checker",
      "SLA Orchestration",
      "Business Rules",
      "Exception Handling",
      "Process Automation",
    ],
    bg: "linear-gradient(180deg, #DBEAFE 0%, #BFDBFE 100%)",
    border: "rgba(14, 165, 233, 0.24)",
    titleColor: "#0B1F3A",
    chipBg: "rgba(255,255,255,0.92)",
    chipBorder: "rgba(14,165,233,0.28)",
    chipColor: "#0B1F3A",
    tone: "orchestration",
  },
  {
    n: "04",
    Icon: BarChart3,
    title: "Data & Intelligence",
    items: [
      "Operational Analytics",
      "Fraud Detection",
      "Persistency Models",
      "Customer Intelligence",
      "Reporting",
    ],
    bg: "linear-gradient(180deg, #ECFEFF 0%, #CFFAFE 100%)",
    border: "rgba(8, 145, 178, 0.22)",
    titleColor: "#0B1F3A",
    chipBg: "rgba(255,255,255,0.92)",
    chipBorder: "rgba(8,145,178,0.22)",
    chipColor: "#0B1F3A",
    tone: "intelligence",
  },
  {
    n: "05",
    Icon: Cpu,
    title: "Gradatim Intelligence",
    items: [
      "Agentic AI",
      "Claims AI",
      "Underwriting Intelligence",
      "AI Workflows",
      "Predictive Operations",
    ],
    bg: "linear-gradient(180deg, #0B1F3A 0%, #0A1A33 100%)",
    border: "rgba(14, 165, 233, 0.40)",
    titleColor: "#FFFFFF",
    chipBg: "rgba(255,255,255,0.06)",
    chipBorder: "rgba(14,165,233,0.35)",
    chipColor: "#E0F2FE",
    tone: "ai",
    anchor: true,
  },
];

const CAPABILITIES = [
  { Icon: Layers, label: "Composable Architecture" },
  { Icon: Plug, label: "API-First Integration" },
  { Icon: Sparkles, label: "AI-Embedded Workflows" },
  { Icon: ShieldCheck, label: "Enterprise Governance" },
  { Icon: Gauge, label: "High-Volume Scalability" },
  { Icon: Building2, label: "Multi-Line Insurance Support" },
];

const PlatformPositioning = () => {
  return (
    <section
      id="positioning"
      data-testid="platform-positioning"
      className="relative bg-white border-t border-hairline overflow-hidden"
    >
      {/* Subtle architectural backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(11,31,58,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,58,0.035) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(14,165,233,0.06), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
            style={{
              background: "rgba(14,165,233,0.08)",
              borderColor: "rgba(14,165,233,0.22)",
            }}
            data-testid="positioning-eyebrow"
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: ACCENT }}
            />
            <span
              className="text-[11px] font-medium uppercase tracking-[0.22em]"
              style={{ color: ACCENT }}
            >
              Unified Platform Architecture
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] mt-6"
            style={{ color: DEEP }}
            data-testid="positioning-headline"
          >
            Built as a Unified Insurance{" "}
            <span style={{ color: ACCENT }}>Operating Platform</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-base md:text-[17px] text-slate-600 leading-relaxed max-w-[720px] mx-auto"
            data-testid="positioning-subcopy"
          >
            Gradatim connects policy administration, digital distribution,
            workflow orchestration, APIs, analytics, ecosystem integrations,
            and AI-driven operations into a single composable insurance
            platform.
          </motion.p>
        </div>

        {/* Platform stack */}
        <div
          className="relative mt-16 md:mt-20"
          data-testid="platform-stack"
        >
          {/* Soft halo behind stack */}
          <div
            aria-hidden
            className="absolute -inset-x-10 -inset-y-6 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(14,165,233,0.06), transparent 70%)",
            }}
          />

          <div className="relative flex flex-col gap-3 md:gap-3.5">
            {LAYERS.map((layer, idx) => (
              <PlatformLayer
                key={layer.n}
                layer={layer}
                idx={idx}
                isLast={idx === LAYERS.length - 1}
              />
            ))}
          </div>
        </div>

        {/* Capability strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 md:mt-16 flex flex-wrap items-center justify-center gap-2.5 md:gap-3"
          data-testid="capability-strip"
        >
          {CAPABILITIES.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.5,
                delay: 0.05 + i * 0.04,
                ease: [0.16, 1, 0.3, 1],
              }}
              whileHover={{ y: -2 }}
              className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full border bg-white transition-shadow hover:shadow-md"
              style={{ borderColor: "rgba(11,31,58,0.12)" }}
            >
              <c.Icon
                className="h-3.5 w-3.5"
                style={{ color: ACCENT }}
                strokeWidth={2}
              />
              <span
                className="text-[12.5px] font-medium tracking-tight"
                style={{ color: DEEP }}
              >
                {c.label}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Supporting enterprise statement */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 md:mt-16 mx-auto text-center font-display text-xl sm:text-2xl md:text-[28px] leading-[1.35] tracking-tight max-w-[920px]"
          style={{ color: DEEP }}
          data-testid="positioning-statement"
        >
          One <span style={{ color: ACCENT }}>governed platform</span> for
          policy, claims, underwriting, distribution, workflows, analytics,
          and AI-led insurance operations.
        </motion.p>
      </div>
    </section>
  );
};

const PlatformLayer = ({ layer, idx, isLast }) => {
  const { Icon, title, items, bg, border, titleColor, chipBg, chipBorder, chipColor, n, anchor } = layer;
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.65,
        delay: 0.08 + idx * 0.08,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ y: -3 }}
      className="relative group"
      data-testid={`platform-layer-${idx + 1}`}
    >
      {/* Connector line to next layer */}
      {!isLast && (
        <div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 -bottom-3 md:-bottom-3.5 z-10 flex flex-col items-center"
        >
          <span
            className="block w-px h-3 md:h-3.5"
            style={{
              background:
                "linear-gradient(180deg, rgba(14,165,233,0.55), rgba(14,165,233,0.0))",
            }}
          />
        </div>
      )}

      <div
        className={`relative rounded-2xl overflow-hidden transition-shadow ${
          anchor ? "shadow-[0_20px_60px_-20px_rgba(14,165,233,0.45)]" : "shadow-sm hover:shadow-md"
        }`}
        style={{
          background: bg,
          border: `1px solid ${border}`,
        }}
      >
        {/* AI layer glow */}
        {anchor && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 60% 80% at 80% 50%, rgba(14,165,233,0.18), transparent 70%), radial-gradient(ellipse 40% 60% at 10% 50%, rgba(14,165,233,0.10), transparent 70%)",
            }}
          />
        )}

        <div className="relative px-5 md:px-8 py-5 md:py-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          {/* Left: layer index + icon + title */}
          <div className="flex items-center gap-4 md:min-w-[280px] md:max-w-[300px]">
            <div
              className="flex items-center justify-center h-11 w-11 rounded-xl shrink-0"
              style={{
                background: anchor ? "rgba(14,165,233,0.15)" : "rgba(14,165,233,0.10)",
                border: `1px solid ${anchor ? "rgba(14,165,233,0.45)" : "rgba(14,165,233,0.22)"}`,
              }}
            >
              <Icon
                className="h-5 w-5"
                style={{ color: anchor ? "#7DD3FC" : ACCENT }}
                strokeWidth={1.8}
              />
            </div>
            <div className="min-w-0">
              <div
                className="text-[10.5px] font-medium tracking-[0.22em] uppercase"
                style={{
                  color: anchor ? "rgba(224,242,254,0.65)" : "rgba(11,31,58,0.55)",
                }}
              >
                Layer {n}
              </div>
              <h3
                className="font-display text-lg md:text-xl tracking-tight leading-tight mt-1"
                style={{ color: titleColor }}
              >
                {title}
              </h3>
            </div>
          </div>

          {/* Right: capability chips */}
          <div className="flex flex-wrap items-center gap-2 md:gap-2.5 flex-1">
            {items.map((it) => (
              <span
                key={it}
                className="inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-medium transition-all"
                style={{
                  background: chipBg,
                  border: `1px solid ${chipBorder}`,
                  color: chipColor,
                  backdropFilter: anchor ? "blur(8px)" : "none",
                  WebkitBackdropFilter: anchor ? "blur(8px)" : "none",
                }}
              >
                {it}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION 3 — PLATFORM ARCHITECTURE DIAGRAM
   Vertical connected architecture with central spine, port nodes,
   traveling signal pulses, and a glowing AI anchor at the bottom.
   ───────────────────────────────────────────────────────────────────── */

const ARCH_LAYERS = [
  {
    title: "Experience Layer",
    tag: "Customer & Distribution",
    blocks: [
      "Employer Portals",
      "Bancassurance",
      "Embedded Insurance",
      "Broker Platforms",
      "APIs",
      "Mobile Journeys",
    ],
    tone: "neutral",
  },
  {
    title: "Core Insurance Layer",
    tag: "Operational Engine",
    blocks: [
      "Product Configuration",
      "Policy Administration",
      "Claims",
      "Renewals",
      "Underwriting",
      "Accounting",
      "Reinsurance",
    ],
    tone: "blue",
  },
  {
    title: "Workflow & Rules Layer",
    tag: "Governance & Orchestration",
    blocks: [
      "Maker-Checker",
      "SLA Orchestration",
      "Business Rules",
      "Exception Handling",
      "Automation",
    ],
    tone: "deepblue",
  },
  {
    title: "Data & Intelligence Layer",
    tag: "Analytics & Decision Support",
    blocks: [
      "Analytics",
      "Operational Intelligence",
      "Fraud Models",
      "Persistency AI",
      "Customer Intelligence",
    ],
    tone: "cyan",
  },
  {
    title: "Gradatim Intelligence",
    tag: "AI-Native Operations",
    blocks: [
      "Agentic AI",
      "Claims AI",
      "AI-Assisted Operations",
      "AI Workflows",
      "AI Copilots",
    ],
    tone: "ai",
  },
];

const PlatformArchitecture = () => {
  return (
    <section
      id="architecture"
      data-testid="platform-architecture"
      className="relative bg-white border-t border-hairline overflow-hidden"
    >
      {/* Blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.4]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(14,165,233,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,58,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse at 50% 40%, rgba(0,0,0,0.7), transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 40%, rgba(0,0,0,0.7), transparent 85%)",
        }}
      />
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 35% at 50% 100%, rgba(14,165,233,0.10), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
            style={{
              background: "rgba(14,165,233,0.08)",
              borderColor: "rgba(14,165,233,0.22)",
            }}
            data-testid="architecture-eyebrow"
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: ACCENT }}
            />
            <span
              className="text-[11px] font-medium uppercase tracking-[0.22em]"
              style={{ color: ACCENT }}
            >
              Platform Architecture
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] mt-6"
            style={{ color: DEEP }}
            data-testid="architecture-headline"
          >
            Composable Insurance Infrastructure{" "}
            <span style={{ color: ACCENT }}>Built for Scale</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-base md:text-[17px] text-slate-600 leading-relaxed max-w-[760px] mx-auto"
            data-testid="architecture-subcopy"
          >
            Gradatim unifies policy administration, digital distribution,
            workflows, analytics, ecosystem integrations, and AI-driven
            operations into a connected insurance operating architecture.
          </motion.p>
        </div>

        {/* Architecture diagram */}
        <div
          className="relative mt-20 md:mt-24"
          data-testid="architecture-diagram"
        >
          {/* Central spine */}
          <div
            aria-hidden
            className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px overflow-hidden pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(14,165,233,0.0) 0%, rgba(14,165,233,0.35) 8%, rgba(14,165,233,0.45) 50%, rgba(14,165,233,0.55) 92%, rgba(14,165,233,0.0) 100%)",
            }}
          >
            {/* Traveling signal pulses */}
            <motion.span
              className="absolute left-1/2 -translate-x-1/2 h-12 w-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(180deg, rgba(14,165,233,0) 0%, rgba(14,165,233,0.95) 50%, rgba(14,165,233,0) 100%)",
                boxShadow: "0 0 12px rgba(14,165,233,0.7)",
              }}
              animate={{ top: ["-12%", "108%"] }}
              transition={{ duration: 4.4, repeat: Infinity, ease: "linear" }}
            />
            <motion.span
              className="absolute left-1/2 -translate-x-1/2 h-12 w-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(180deg, rgba(14,165,233,0) 0%, rgba(125,211,252,0.85) 50%, rgba(14,165,233,0) 100%)",
                boxShadow: "0 0 10px rgba(125,211,252,0.6)",
              }}
              animate={{ top: ["-12%", "108%"] }}
              transition={{
                duration: 4.4,
                delay: 2.2,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          </div>

          <div className="relative flex flex-col gap-7 md:gap-9">
            {ARCH_LAYERS.map((layer, idx) => (
              <ArchitectureNode
                key={layer.title}
                layer={layer}
                idx={idx}
                isLast={idx === ARCH_LAYERS.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TONE_STYLES = {
  neutral: {
    cardBg: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
    border: "rgba(15,23,42,0.10)",
    titleColor: "#0B1F3A",
    tagColor: "rgba(11,31,58,0.55)",
    blockBg: "#FFFFFF",
    blockBorder: "rgba(15,23,42,0.10)",
    blockText: "#0B1F3A",
    portColor: "rgba(15,23,42,0.30)",
    portRing: "rgba(15,23,42,0.10)",
    shadow: "0 4px 24px -8px rgba(15,23,42,0.10)",
  },
  blue: {
    cardBg: "linear-gradient(180deg, #FFFFFF 0%, #EFF6FF 100%)",
    border: "rgba(14,165,233,0.22)",
    titleColor: "#0B1F3A",
    tagColor: "rgba(11,31,58,0.55)",
    blockBg: "rgba(255,255,255,0.95)",
    blockBorder: "rgba(14,165,233,0.20)",
    blockText: "#0B1F3A",
    portColor: ACCENT,
    portRing: "rgba(14,165,233,0.20)",
    shadow: "0 6px 28px -10px rgba(14,165,233,0.25)",
  },
  deepblue: {
    cardBg: "linear-gradient(180deg, #EFF6FF 0%, #DBEAFE 100%)",
    border: "rgba(14,165,233,0.28)",
    titleColor: "#0B1F3A",
    tagColor: "rgba(11,31,58,0.6)",
    blockBg: "rgba(255,255,255,0.95)",
    blockBorder: "rgba(14,165,233,0.28)",
    blockText: "#0B1F3A",
    portColor: ACCENT,
    portRing: "rgba(14,165,233,0.28)",
    shadow: "0 8px 30px -12px rgba(14,165,233,0.30)",
  },
  cyan: {
    cardBg: "linear-gradient(180deg, #ECFEFF 0%, #CFFAFE 100%)",
    border: "rgba(8,145,178,0.25)",
    titleColor: "#0B1F3A",
    tagColor: "rgba(11,31,58,0.6)",
    blockBg: "rgba(255,255,255,0.95)",
    blockBorder: "rgba(8,145,178,0.25)",
    blockText: "#0B1F3A",
    portColor: "#0891B2",
    portRing: "rgba(8,145,178,0.25)",
    shadow: "0 8px 30px -12px rgba(8,145,178,0.30)",
  },
  ai: {
    cardBg: "linear-gradient(180deg, #0B1F3A 0%, #06132A 100%)",
    border: "rgba(14,165,233,0.45)",
    titleColor: "#FFFFFF",
    tagColor: "rgba(224,242,254,0.70)",
    blockBg: "rgba(255,255,255,0.06)",
    blockBorder: "rgba(14,165,233,0.40)",
    blockText: "#E0F2FE",
    portColor: "#7DD3FC",
    portRing: "rgba(125,211,252,0.40)",
    shadow:
      "0 24px 70px -20px rgba(14,165,233,0.55), 0 0 0 1px rgba(14,165,233,0.18)",
  },
};

const ArchitectureNode = ({ layer, idx, isLast }) => {
  const t = TONE_STYLES[layer.tone];
  const isAI = layer.tone === "ai";
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.7,
        delay: 0.08 + idx * 0.08,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ y: -3 }}
      className="relative"
      data-testid={`architecture-node-${idx + 1}`}
    >
      {/* Top port node */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 -top-2.5 z-20 flex items-center justify-center"
      >
        <span
          className="block h-2.5 w-2.5 rounded-full"
          style={{
            background: t.portColor,
            boxShadow: `0 0 0 4px ${t.portRing}, 0 0 12px ${
              isAI ? "rgba(125,211,252,0.7)" : "rgba(14,165,233,0.5)"
            }`,
          }}
        />
      </div>

      {/* Bottom port node (skip on last) */}
      {!isLast && (
        <div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 -bottom-2.5 z-20 flex items-center justify-center"
        >
          <span
            className="block h-2.5 w-2.5 rounded-full"
            style={{
              background: t.portColor,
              boxShadow: `0 0 0 4px ${t.portRing}, 0 0 12px ${
                isAI ? "rgba(125,211,252,0.7)" : "rgba(14,165,233,0.5)"
              }`,
            }}
          />
        </div>
      )}

      {/* Layer card */}
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: t.cardBg,
          border: `1px solid ${t.border}`,
          boxShadow: t.shadow,
        }}
      >
        {/* AI ambient */}
        {isAI && (
          <>
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(14,165,233,0.22), transparent 70%)",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.15] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(125,211,252,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(125,211,252,0.6) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
                maskImage:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
                WebkitMaskImage:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
              }}
            />
          </>
        )}

        <div className="relative px-6 md:px-10 py-7 md:py-9">
          {/* Header row */}
          <div className="flex items-end justify-between gap-6 mb-5 md:mb-6">
            <div>
              <div
                className="text-[10.5px] font-medium tracking-[0.22em] uppercase mb-2"
                style={{ color: t.tagColor }}
              >
                {layer.tag}
              </div>
              <h3
                className="font-display text-xl md:text-2xl tracking-tight leading-tight"
                style={{ color: t.titleColor }}
              >
                {layer.title}
              </h3>
            </div>
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <span
                className="text-[10.5px] font-medium tracking-[0.22em] uppercase"
                style={{ color: t.tagColor }}
              >
                Layer
              </span>
              <span
                className="font-display text-base tracking-tight"
                style={{ color: t.titleColor, opacity: 0.85 }}
              >
                / 0{idx + 1}
              </span>
            </div>
          </div>

          {/* Architectural sub-blocks */}
          <div
            className="grid gap-2 md:gap-2.5"
            style={{
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
            }}
          >
            {layer.blocks.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{
                  duration: 0.45,
                  delay: 0.15 + i * 0.03,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg transition-all"
                style={{
                  background: t.blockBg,
                  border: `1px solid ${t.blockBorder}`,
                  backdropFilter: isAI ? "blur(6px)" : "none",
                  WebkitBackdropFilter: isAI ? "blur(6px)" : "none",
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{
                    background: t.portColor,
                    boxShadow: isAI
                      ? "0 0 8px rgba(125,211,252,0.6)"
                      : "0 0 6px rgba(14,165,233,0.35)",
                  }}
                />
                <span
                  className="text-[12.5px] font-medium tracking-tight truncate"
                  style={{ color: t.blockText }}
                >
                  {b}
                </span>
              </motion.div>
            ))}
          </div>

          {/* AI bottom signature */}
          {isAI && (
            <div className="mt-6 flex items-center gap-2.5">
              <span
                className="h-1.5 w-1.5 rounded-full animate-pulse"
                style={{ background: "#7DD3FC" }}
              />
              <span
                className="text-[11px] font-medium uppercase tracking-[0.22em]"
                style={{ color: "#7DD3FC" }}
              >
                Intelligence Engine · Always On
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};


/* ─────────────────────────────────────────────────────────────────────
   SECTION 4 — BUSINESS OUTCOMES
   ───────────────────────────────────────────────────────────────────── */

const OUTCOME_PILLARS = [
  {
    Icon: TrendingUp,
    label: "Growth",
    accent: "#0EA5E9",
    items: [
      "Embedded insurance",
      "Faster product launches",
      "New distribution channels",
      "Ecosystem scale",
    ],
  },
  {
    Icon: Gauge,
    label: "Operations",
    accent: "#0891B2",
    items: [
      "Lower servicing effort",
      "Straight-through processing",
      "Automated workflows",
      "SLA-governed orchestration",
    ],
  },
  {
    Icon: Code2,
    label: "Technology",
    accent: "#6366F1",
    items: [
      "API-first modernization",
      "Modular deployment",
      "Legacy replacement",
      "Cloud-ready infrastructure",
    ],
  },
  {
    Icon: BrainCircuit,
    label: "AI Transformation",
    accent: "#0B1F3A",
    items: [
      "Claims intelligence",
      "Fraud detection",
      "Operational AI",
      "Predictive analytics",
    ],
    anchor: true,
  },
];

const BusinessOutcomes = () => {
  return (
    <section
      id="outcomes"
      data-testid="business-outcomes"
      className="relative bg-cream border-t border-hairline overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(14,165,233,0.06), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border bg-white"
            style={{ borderColor: "rgba(14,165,233,0.22)" }}
            data-testid="outcomes-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
            <span className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
              Business Outcomes
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] mt-6"
            style={{ color: DEEP }}
            data-testid="outcomes-headline"
          >
            What insurers <span style={{ color: ACCENT }}>achieve</span> with Gradatim
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-base md:text-[17px] text-slate-600 leading-relaxed max-w-[720px] mx-auto"
          >
            Beyond features. Real, measurable shifts across growth, operations,
            technology and AI — delivered as one connected platform.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-16 md:mt-20">
          {OUTCOME_PILLARS.map((p, idx) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: 0.08 + idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -4 }}
              className="relative rounded-2xl p-7 md:p-8 transition-shadow group"
              style={{
                background: p.anchor
                  ? "linear-gradient(180deg, #0B1F3A 0%, #06132A 100%)"
                  : "linear-gradient(180deg, #FFFFFF 0%, #FAFCFE 100%)",
                border: p.anchor ? "1px solid rgba(14,165,233,0.40)" : "1px solid rgba(11,31,58,0.08)",
                boxShadow: p.anchor
                  ? "0 20px 60px -20px rgba(14,165,233,0.45)"
                  : "0 4px 28px -12px rgba(11,31,58,0.10)",
              }}
              data-testid={`outcome-pillar-${idx + 1}`}
            >
              {p.anchor && (
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-2xl pointer-events-none opacity-50"
                  style={{
                    background:
                      "radial-gradient(ellipse 80% 60% at 80% 100%, rgba(14,165,233,0.20), transparent 70%)",
                  }}
                />
              )}
              <div
                className="relative inline-flex items-center justify-center h-11 w-11 rounded-xl mb-6"
                style={{
                  background: p.anchor ? "rgba(14,165,233,0.18)" : `${p.accent}14`,
                  border: `1px solid ${p.anchor ? "rgba(14,165,233,0.45)" : `${p.accent}33`}`,
                }}
              >
                <p.Icon
                  className="h-5 w-5"
                  style={{ color: p.anchor ? "#7DD3FC" : p.accent }}
                  strokeWidth={1.8}
                />
              </div>
              <h3
                className="relative font-display text-xl md:text-[22px] tracking-tight leading-tight mb-5"
                style={{ color: p.anchor ? "#FFFFFF" : DEEP }}
              >
                {p.label}
              </h3>
              <ul className="relative space-y-3">
                {p.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5">
                    <span
                      className="mt-2 h-1 w-1 rounded-full shrink-0"
                      style={{ background: p.anchor ? "#7DD3FC" : p.accent }}
                    />
                    <span
                      className="text-[13.5px] leading-relaxed"
                      style={{ color: p.anchor ? "rgba(224,242,254,0.85)" : "#475569" }}
                    >
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION 5 — INDUSTRY USE CASES
   ───────────────────────────────────────────────────────────────────── */

const USE_CASES = [
  { Icon: Users, title: "Group Insurance", desc: "Large-employer life & health programs at scale." },
  { Icon: CreditCard, title: "Credit Life", desc: "Lender-tied protection across portfolios and partners." },
  { Icon: HeartPulse, title: "Health", desc: "Member onboarding, claims and wellness journeys." },
  { Icon: Plug, title: "Embedded Insurance", desc: "Insurance built into partner products and APIs." },
  { Icon: Building2, title: "Bancassurance", desc: "Bank-distributed life and protection at branch scale." },
  { Icon: PiggyBank, title: "Pension & Funds", desc: "Long-term savings, annuities and fund administration." },
  { Icon: Award, title: "Affinity", desc: "Group, association and co-branded distribution." },
  { Icon: Coins, title: "Byte-sized Insurance", desc: "Micro-policies, sachet covers and on-demand insurance." },
];

const IndustryUseCases = () => {
  return (
    <section
      id="use-cases"
      data-testid="industry-use-cases"
      className="relative bg-white border-t border-hairline overflow-hidden"
    >
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
              style={{ background: "rgba(14,165,233,0.08)", borderColor: "rgba(14,165,233,0.22)" }}
              data-testid="usecases-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
              <span className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
                Industry Use Cases
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-[52px] tracking-tight leading-[1.05] mt-6"
              style={{ color: DEEP }}
              data-testid="usecases-headline"
            >
              Built for the way insurance is <span style={{ color: ACCENT }}>actually distributed</span>.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="text-[15px] text-slate-600 leading-relaxed max-w-md md:text-right"
          >
            Eight verticals. One platform. Configured to the way each line of business operates today — and tomorrow.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {USE_CASES.map((u, i) => (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: 0.05 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -4 }}
              className="group relative rounded-xl p-6 md:p-7 bg-white transition-all"
              style={{
                border: "1px solid rgba(11,31,58,0.08)",
                boxShadow: "0 2px 10px -4px rgba(11,31,58,0.06)",
              }}
              data-testid={`usecase-${i + 1}`}
            >
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(14,165,233,0.6) 50%, transparent 100%)",
                }}
              />
              <div
                className="inline-flex items-center justify-center h-10 w-10 rounded-lg mb-5"
                style={{
                  background: "rgba(14,165,233,0.10)",
                  border: "1px solid rgba(14,165,233,0.22)",
                }}
              >
                <u.Icon className="h-4 w-4" style={{ color: ACCENT }} strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-[18px] tracking-tight leading-tight mb-2" style={{ color: DEEP }}>
                {u.title}
              </h3>
              <p className="text-[13px] text-slate-600 leading-relaxed">{u.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION 6 — GRADATIM INTELLIGENCE (AI inside the platform)
   ───────────────────────────────────────────────────────────────────── */

const AI_PRODUCTS = [
  {
    Icon: Sparkles,
    title: "Claims AI",
    desc: "Faster, fairer claims decisions with embedded intelligence across triage, adjudication and settlement.",
  },
  {
    Icon: ShieldAlert,
    title: "Fraud AI",
    desc: "Pattern-based fraud detection at scale across high-volume policy and claims data.",
  },
  {
    Icon: TrendingDown,
    title: "Persistency AI",
    desc: "Predicts lapse risk and surfaces targeted retention actions across the book.",
  },
];

const AI_CLIENTS = ["Tata AIA", "SBI Life", "Canara HSBC"];

const GradatimIntelligence = () => {
  return (
    <section
      id="intelligence"
      data-testid="gradatim-intelligence"
      data-nav-dark
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #06132A 0%, #0B1F3A 100%)" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(125,211,252,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(125,211,252,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 40%, rgba(0,0,0,0.7), transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 40%, rgba(0,0,0,0.7), transparent 80%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 80% 20%, rgba(14,165,233,0.20), transparent 70%), radial-gradient(ellipse 60% 40% at 20% 80%, rgba(125,211,252,0.10), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: "rgba(14,165,233,0.10)",
              border: "1px solid rgba(14,165,233,0.40)",
            }}
            data-testid="intelligence-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: "#7DD3FC" }} />
            <span className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: "#7DD3FC" }}>
              Gradatim Intelligence
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] lg:text-[52px] tracking-tight leading-[1.08] mt-6 text-white"
            data-testid="intelligence-headline"
          >
            Embedded operational <span style={{ color: "#7DD3FC" }}>intelligence</span>
            <br />
            — not experimental AI.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-base md:text-[17px] leading-relaxed max-w-[760px] mx-auto"
            style={{ color: "rgba(224,242,254,0.75)" }}
          >
            AI lives inside the platform — orchestrating claims, surfacing fraud,
            predicting persistency and powering decisions in the same workflows
            insurers already run.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-16 md:mt-20">
          {AI_PRODUCTS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: 0.08 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -4 }}
              className="relative rounded-2xl p-7 md:p-8 backdrop-blur-md transition-shadow"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(125,211,252,0.20)",
                boxShadow: "0 10px 40px -20px rgba(14,165,233,0.4)",
              }}
              data-testid={`ai-product-${i + 1}`}
            >
              <div
                className="inline-flex items-center justify-center h-11 w-11 rounded-xl mb-6"
                style={{
                  background: "rgba(14,165,233,0.18)",
                  border: "1px solid rgba(14,165,233,0.40)",
                }}
              >
                <p.Icon className="h-5 w-5" style={{ color: "#7DD3FC" }} strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-xl md:text-[22px] tracking-tight leading-tight mb-3 text-white">
                {p.title}
              </h3>
              <p className="text-[14px] leading-relaxed" style={{ color: "rgba(224,242,254,0.75)" }}>
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-20"
          data-testid="ai-clients"
        >
          <div className="flex items-center justify-center gap-3 mb-7">
            <span className="h-px w-12" style={{ background: "rgba(125,211,252,0.30)" }} />
            <span className="text-[10.5px] font-medium uppercase tracking-[0.28em]" style={{ color: "rgba(125,211,252,0.8)" }}>
              Deployed at
            </span>
            <span className="h-px w-12" style={{ background: "rgba(125,211,252,0.30)" }} />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 md:gap-x-16 gap-y-5">
            {AI_CLIENTS.map((c) => (
              <span
                key={c}
                className="font-display text-lg md:text-xl tracking-[0.12em] uppercase"
                style={{ color: "rgba(255,255,255,0.78)" }}
              >
                {c}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION 7 — SCALE & PROOF
   ───────────────────────────────────────────────────────────────────── */

const PROOF_METRICS = [
  { value: "160M+", label: "Policies & members processed annually" },
  { value: "20+", label: "Insurers running on Gradatim" },
  { value: "120+", label: "Product variants live across lines" },
  { value: "30%+", label: "Of India's group-life processing" },
];

const PROOF_CAPABILITIES = [
  { Icon: Globe2, label: "Multi-country capability" },
  { Icon: Plug, label: "API-first" },
  { Icon: Cloud, label: "Cloud-ready" },
  { Icon: Cpu, label: "AI-ready" },
];

const PROOF_CLIENTS = [
  "LIC of India",
  "Aditya Birla Sun Life Insurance",
  "Aditya Birla Health",
  "Pramerica Life Insurance",
];

const ScaleAndProof = () => {
  return (
    <section
      id="proof"
      data-testid="scale-proof"
      className="relative bg-white border-t border-hairline overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(11,31,58,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,58,0.04) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
            style={{ background: "rgba(14,165,233,0.08)", borderColor: "rgba(14,165,233,0.22)" }}
            data-testid="proof-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
            <span className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
              Scale & Proof
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] mt-6"
            style={{ color: DEEP }}
            data-testid="proof-headline"
          >
            Running insurance operations <span style={{ color: ACCENT }}>at national scale</span>.
          </motion.h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mt-16 md:mt-20" data-testid="proof-metrics">
          {PROOF_METRICS.map((m, i) => (
            <motion.div
              key={m.value}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.05 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl p-6 md:p-8"
              style={{
                background: "linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)",
                border: "1px solid rgba(14,165,233,0.18)",
                boxShadow: "0 8px 30px -12px rgba(11,31,58,0.10)",
              }}
            >
              <div
                className="font-display font-medium text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-none"
                style={{ color: DEEP }}
              >
                {m.value}
              </div>
              <div className="mt-3 text-[13px] md:text-[13.5px] text-slate-600 leading-snug">
                {m.label}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3 mt-12 md:mt-14"
        >
          {PROOF_CAPABILITIES.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white"
              style={{ border: "1px solid rgba(11,31,58,0.12)" }}
            >
              <c.Icon className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={2} />
              <span className="text-[12.5px] font-medium tracking-tight" style={{ color: DEEP }}>
                {c.label}
              </span>
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-20"
          data-testid="proof-clients"
        >
          <div className="flex items-center justify-center gap-3 mb-7">
            <span className="h-px w-12" style={{ background: "rgba(11,31,58,0.15)" }} />
            <span className="text-[10.5px] font-medium uppercase tracking-[0.28em] text-slate-500">
              Trusted by leading insurers
            </span>
            <span className="h-px w-12" style={{ background: "rgba(11,31,58,0.15)" }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-6 items-center">
            {PROOF_CLIENTS.map((c) => (
              <div
                key={c}
                className="text-center font-display text-[15px] md:text-base tracking-[0.12em] uppercase transition-colors hover:text-ink"
                style={{ color: "rgba(11,31,58,0.55)" }}
              >
                {c}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION 8 — MODERNIZATION / WHY GRADATIM
   ───────────────────────────────────────────────────────────────────── */

const COMPARISON = [
  { legacy: "Slow CR cycles", gradatim: "AI-accelerated delivery" },
  { legacy: "Monolithic core", gradatim: "Composable, modular platform" },
  { legacy: "Closed systems", gradatim: "API-first integration" },
  { legacy: "Static workflows", gradatim: "Intelligent orchestration" },
  { legacy: "Manual operations", gradatim: "Embedded operational AI" },
  { legacy: "On-prem, hard to scale", gradatim: "Cloud-ready, multi-country scale" },
];

const Modernization = () => {
  return (
    <section
      id="modernization"
      data-testid="modernization"
      className="relative bg-cream border-t border-hairline overflow-hidden"
    >
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border bg-white"
            style={{ borderColor: "rgba(14,165,233,0.22)" }}
            data-testid="modernization-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
            <span className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
              Modernization
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] mt-6"
            style={{ color: DEEP }}
            data-testid="modernization-headline"
          >
            Designed for the <span style={{ color: ACCENT }}>next generation</span> of insurance.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-base md:text-[17px] text-slate-600 leading-relaxed max-w-[680px] mx-auto"
          >
            A direct path off legacy PAS — without freezing the business while you do it.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-16 md:mt-20"
          data-testid="modernization-comparison"
        >
          <div
            className="relative rounded-2xl p-7 md:p-9"
            style={{
              background: "linear-gradient(180deg, #FFFFFF 0%, #F4F4F5 100%)",
              border: "1px solid rgba(11,31,58,0.10)",
            }}
          >
            <div className="flex items-center gap-3 mb-7">
              <span
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg"
                style={{ background: "rgba(11,31,58,0.06)", border: "1px solid rgba(11,31,58,0.10)" }}
              >
                <X className="h-4 w-4 text-slate-500" strokeWidth={2} />
              </span>
              <div>
                <div className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-slate-500">Legacy PAS</div>
                <h3 className="font-display text-xl tracking-tight" style={{ color: DEEP }}>
                  The old way
                </h3>
              </div>
            </div>
            <ul className="space-y-4">
              {COMPARISON.map((row) => (
                <li
                  key={row.legacy}
                  className="flex items-start gap-3 pb-4 border-b last:border-0 last:pb-0"
                  style={{ borderColor: "rgba(11,31,58,0.06)" }}
                >
                  <X className="h-4 w-4 mt-1 shrink-0 text-slate-400" strokeWidth={2} />
                  <span className="text-[14.5px] text-slate-600 leading-relaxed">{row.legacy}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="relative rounded-2xl p-7 md:p-9 overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #0B1F3A 0%, #06132A 100%)",
              border: "1px solid rgba(14,165,233,0.40)",
              boxShadow: "0 20px 60px -20px rgba(14,165,233,0.45)",
            }}
          >
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 70% 50% at 80% 0%, rgba(14,165,233,0.20), transparent 70%)",
              }}
            />
            <div className="relative flex items-center gap-3 mb-7">
              <span
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg"
                style={{ background: "rgba(14,165,233,0.18)", border: "1px solid rgba(14,165,233,0.40)" }}
              >
                <Check className="h-4 w-4" style={{ color: "#7DD3FC" }} strokeWidth={2.5} />
              </span>
              <div>
                <div className="text-[10.5px] font-medium uppercase tracking-[0.22em]" style={{ color: "rgba(125,211,252,0.85)" }}>
                  Gradatim
                </div>
                <h3 className="font-display text-xl tracking-tight text-white">
                  The next generation
                </h3>
              </div>
            </div>
            <ul className="relative space-y-4">
              {COMPARISON.map((row) => (
                <li
                  key={row.gradatim}
                  className="flex items-start gap-3 pb-4 border-b last:border-0 last:pb-0"
                  style={{ borderColor: "rgba(125,211,252,0.12)" }}
                >
                  <Check className="h-4 w-4 mt-1 shrink-0" style={{ color: "#7DD3FC" }} strokeWidth={2.5} />
                  <span className="text-[14.5px] leading-relaxed" style={{ color: "rgba(224,242,254,0.92)" }}>
                    {row.gradatim}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION 9 — FINAL CTA
   ───────────────────────────────────────────────────────────────────── */

const FinalCTA = () => {
  return (
    <section
      id="final-cta"
      data-testid="final-cta"
      data-nav-dark
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #06132A 0%, #0B1F3A 100%)" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(125,211,252,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(125,211,252,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 40% at 50% 100%, rgba(14,165,233,0.25), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
          style={{
            background: "rgba(14,165,233,0.10)",
            border: "1px solid rgba(14,165,233,0.40)",
          }}
        >
          <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: "#7DD3FC" }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: "#7DD3FC" }}>
            Build with Gradatim
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-medium text-4xl sm:text-5xl md:text-6xl lg:text-[68px] tracking-tight leading-[1.02] mt-7 text-white"
          data-testid="final-cta-headline"
        >
          Build the next insurance{" "}
          <span style={{ color: "#7DD3FC" }}>platform</span> with Gradatim.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 text-base md:text-[18px] leading-relaxed max-w-[640px] mx-auto"
          style={{ color: "rgba(224,242,254,0.78)" }}
        >
          One governed, composable, AI-native operating platform — ready to run your
          policy administration, distribution, workflows, analytics and intelligence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex justify-center"
        >
          <Link
            to="/#contact"
            data-testid="final-cta-primary"
            className="group inline-flex items-center justify-center gap-3 text-white px-9 py-4 rounded-md text-sm font-medium tracking-wide transition-all shadow-lg hover:shadow-2xl"
            style={{
              background: "linear-gradient(180deg, #0EA5E9 0%, #0284C7 100%)",
              boxShadow: "0 10px 40px -10px rgba(14,165,233,0.6)",
            }}
          >
            Request a demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

